/**
 * 地図タブの表示用の計算（純粋関数と小さな状態機械）。DOM と Leaflet には触れない。
 * 得点・判定はすべてサーバーが決める。ここで計算するのは表示用の文字と時刻だけ。
 */
import type { MapViewInput } from '../../api/rpc';
import type { ActiveEffect, GameState, RevealedSpot, Scout, ScoutTeam, StateArea, StateEvent, StateSpot } from '../../api/types';

// ---------------------------------------------------------------- 色

/** README ⑮：切手から取った4色。エリアの並び順で割り当てる */
export const AREA_COLORS = ['#A22C3F', '#985C14', '#23487D', '#3D602B'] as const;
const NAMED: Record<string, string> = { red: AREA_COLORS[0], amber: AREA_COLORS[1], blue: AREA_COLORS[2], green: AREA_COLORS[3] };

/** state のエリアの色（#16進か red/amber/blue/green ならそれを使う）。なければ並び順の色 */
export function areaColor(index: number, color: string | null | undefined): string {
  if (color) {
    if (/^#[0-9a-fA-F]{6}$/.test(color)) return color;
    const n = NAMED[color.toLowerCase()];
    if (n) return n;
  }
  return AREA_COLORS[index % AREA_COLORS.length] ?? AREA_COLORS[0];
}

// ---------------------------------------------------------------- 倍率

/** 1.2 → "1.2"、2 → "2.0"、1.25 → "1.25" */
export function formatMult(m: number): string {
  const r = Math.round(m * 100) / 100;
  return Number.isInteger(r * 10) ? r.toFixed(1) : String(r);
}

export type NextMult = { kind: 'cap' } | { kind: 'next'; mult: number; text: string };

/**
 * 次のクリアで何倍か。サーバーの _clear_multiplier と同じ式：LEAST(max, 1 + step × クリア数)。
 * すでに上限なら cap（画面は「上限です」）。clear_max_multiplier が NULL なら上限なし
 */
export function nextMultiplier(area: Pick<StateArea, 'clears' | 'multiplier'>, ev: Pick<StateEvent, 'clear_step_multiplier' | 'clear_max_multiplier'>): NextMult {
  const max = ev.clear_max_multiplier;
  const hasMax = typeof max === 'number' && Number.isFinite(max);
  if (hasMax && area.multiplier >= max - 1e-9) return { kind: 'cap' };
  let next = 1 + ev.clear_step_multiplier * (area.clears + 1);
  if (hasMax) next = Math.min(max, next);
  if (next <= area.multiplier + 1e-9) return { kind: 'cap' };
  return { kind: 'next', mult: next, text: formatMult(next) };
}

// ---------------------------------------------------------------- 帯と詳細の中身

export interface HoneyInfo {
  mult: number;
  endsAt: string | null;
}

export interface HornetInfo {
  /** 襲った側のチーム名 */
  by: string;
  endsAt: string | null;
}

export interface StripItem {
  areaId: string;
  name: string;
  color: string;
  mult: number;
  multText: string;
  clears: number;
  spotsTotal: number;
  spotsLeft: number;
  next: NextMult;
  honey: HoneyInfo | null;
  hornet: HornetInfo | null;
}

function active(e: Pick<ActiveEffect, 'ends_at'>, nowMs: number): boolean {
  return !e.ends_at || Date.parse(e.ends_at) > nowMs;
}

/** 蜜源（multiplier_area）。同じエリアに複数あれば倍率の大きいほう */
export function honeyOf(state: Pick<GameState, 'active_effects'>, areaId: string, nowMs: number): HoneyInfo | null {
  let best: HoneyInfo | null = null;
  for (const e of state.active_effects) {
    if (e.effect_type !== 'multiplier_area' || e.area_id !== areaId || !active(e, nowMs)) continue;
    const mult = e.multiplier ?? 1;
    if (!best || mult > best.mult) best = { mult, endsAt: e.ends_at };
  }
  return best;
}

/** 自チームが襲われているか。area.blocked_until（自チーム向けの値）と、襲った側の名前を出す効果の両方を見る */
export function hornetOf(state: Pick<GameState, 'active_effects'>, area: Pick<StateArea, 'area_id' | 'blocked_until'>, nowMs: number): HornetInfo | null {
  const eff = state.active_effects.find((e) => e.effect_type === 'block_area' && e.area_id === area.area_id && e.targets_me === true && active(e, nowMs));
  const blockedUntil = area.blocked_until && Date.parse(area.blocked_until) > nowMs ? area.blocked_until : null;
  if (!eff && !blockedUntil) return null;
  return { by: eff?.source_team_name ?? '', endsAt: eff?.ends_at ?? blockedUntil };
}

export function buildStrip(state: GameState, nowMs: number): StripItem[] {
  return state.areas.map((a, i) => {
    const sp = state.spots.filter((s) => s.area_id === a.area_id);
    return {
      areaId: a.area_id,
      name: a.name,
      color: areaColor(i, a.color),
      mult: a.multiplier,
      multText: formatMult(a.multiplier),
      clears: a.clears,
      spotsTotal: sp.length,
      spotsLeft: sp.filter((s) => !s.submitted).length,
      next: nextMultiplier(a, state.event),
      honey: honeyOf(state, a.area_id, nowMs),
      hornet: hornetOf(state, a, nowMs),
    };
  });
}

/** 図鑑と同じ番号：state.spots の並びの 1 始まり（運営の並び順＝sort_order と推測） */
export function spotNumber(spots: readonly Pick<StateSpot, 'spot_id'>[], spotId: number): number {
  const i = spots.findIndex((s) => s.spot_id === spotId);
  return i < 0 ? 0 : i + 1;
}

export type SpotButton = { label: string; disabled: boolean; kind: 'submit' | 'done' | 'blocked' };

/** スポット詳細のボタン。状態で文字だけ変わり、枠の大きさは同じ（README ㉑） */
export function spotButton(spot: Pick<StateSpot, 'submitted'>, hornet: HornetInfo | null): SpotButton {
  if (spot.submitted) return { label: 'クリア済み', disabled: true, kind: 'done' };
  if (hornet) return { label: 'スズメバチの襲来中のためクリア不可', disabled: true, kind: 'blocked' };
  return { label: '写真を提出する', disabled: false, kind: 'submit' };
}

// ---------------------------------------------------------------- 縄張り争い

export interface DuelView {
  effectId: number;
  /** 自チームが仕掛けた側か（false＝仕掛けられた側） */
  side: 'src' | 'tgt';
  mine: number;
  theirs: number;
  opponent: string;
  points: number;
  endsAt: string | null;
}

/** 自チームが対戦中ならその状況。他チーム同士の対戦は荷札の通知なので null */
export function duelOf(state: Pick<GameState, 'active_effects' | 'team'>): DuelView | null {
  const myId = state.team.team_id;
  for (const e of state.active_effects) {
    if (e.effect_type !== 'duel' || !e.duel) continue;
    const isSrc = e.source_team === myId;
    const isTgt = e.target_team === myId;
    if (!isSrc && !isTgt) continue;
    return {
      effectId: e.effect_id,
      side: isSrc ? 'src' : 'tgt',
      mine: isSrc ? e.duel.source_count : e.duel.target_count,
      theirs: isSrc ? e.duel.target_count : e.duel.source_count,
      opponent: (isSrc ? e.target_team_name : e.source_team_name) ?? '',
      points: e.duel_points ?? 0,
      endsAt: e.ends_at,
    };
  }
  return null;
}

/** 決まりの一文（仕掛けた側は負けの記述を書かない。同点は仕掛けられた側の勝ち＝サーバー015） */
export function duelRule(side: 'src' | 'tgt', points: number): string {
  return side === 'src' ? `勝てば${points}点` : `勝ちか同点で${points}点`;
}

// ---------------------------------------------------------------- 女王

export interface QueenView {
  key: string;
  effectId: number;
  teamId: string;
  teamName: string;
  spotId: number;
  lat: number;
  lon: number;
  at: string | null;
  /** その女王のチームで一番新しいクリアか（札はここだけに付ける） */
  latest: boolean;
}

/** 他チームの女王の飛行で公開中のスポット。公開中のスポットごとに1匹。自チームのものは出さない */
export function queensOf(state: Pick<GameState, 'revealed' | 'team'>): QueenView[] {
  const mine = state.team.team_id;
  const rows = state.revealed.filter((r): r is RevealedSpot & { spot_id: number; latitude: number; longitude: number } =>
    r.team_id !== mine && r.spot_id !== null && r.latitude !== null && r.longitude !== null,
  );
  const latestBy = new Map<string, number>();
  for (const r of rows) {
    const t = r.at ? Date.parse(r.at) : 0;
    const cur = latestBy.get(`${r.effect_id}`);
    if (cur === undefined || t >= cur) latestBy.set(`${r.effect_id}`, t);
  }
  return rows.map((r) => ({
    key: `${r.effect_id}:${r.spot_id}:${r.at ?? ''}`,
    effectId: r.effect_id,
    teamId: r.team_id,
    teamName: r.team_name,
    spotId: r.spot_id,
    lat: r.latitude,
    lon: r.longitude,
    at: r.at,
    latest: (r.at ? Date.parse(r.at) : 0) === latestBy.get(`${r.effect_id}`),
  }));
}

// ---------------------------------------------------------------- 偵察の円

export interface ScoutCircle {
  effectId: number;
  teamId: string;
  teamName: string;
  lat: number;
  lon: number;
  radiusM: number;
}

/** 自チームの偵察で見えている円（found かつ表示期間内のもの） */
export function scoutCircles(scouts: readonly Scout[], nowMs: number): ScoutCircle[] {
  const out: ScoutCircle[] = [];
  for (const s of scouts) {
    if (s.ends_at && Date.parse(s.ends_at) <= nowMs) continue;
    for (const t of s.teams as ScoutTeam[]) {
      if (!t.found || t.latitude === null || t.longitude === null || t.radius_m === null) continue;
      out.push({ effectId: s.effect_id, teamId: t.team_id, teamName: t.team_name, lat: t.latitude, lon: t.longitude, radiusM: t.radius_m });
    }
  }
  return out;
}

// ---------------------------------------------------------------- GPSの札

export const SLIP_SHOW_MS = 4000;
export const SLIP_REPEAT_MS = 10 * 60 * 1000;

/**
 * 「GPSが取得できていません」の札を出す時刻の管理。取れなくなったらすぐ4秒出し、
 * 取れないままなら10分ごとにまた4秒出す（README ⑱）。取れたら状態を戻す。
 * update(lost, now) を表示中に1秒ごとなどで呼び、返り値が札を出すかどうか。
 */
export class GpsSlipSchedule {
  private lostSince: number | null = null;
  private until = 0;
  private nextAt = 0;

  update(lost: boolean, now: number): boolean {
    if (!lost) {
      this.lostSince = null;
      this.until = 0;
      this.nextAt = 0;
      return false;
    }
    if (this.lostSince === null) {
      this.lostSince = now;
      this.until = now + SLIP_SHOW_MS;
      this.nextAt = now + SLIP_REPEAT_MS;
    } else if (now >= this.nextAt) {
      this.until = now + SLIP_SHOW_MS;
      this.nextAt = now + SLIP_REPEAT_MS;
    }
    return now < this.until;
  }
}

/** geo.status が「取れていない」か。拒否も含める。まだ始まっていない（unknown）は出さない */
export function isGpsLost(status: string): boolean {
  return status === 'none' || status === 'denied';
}

/** 最終取得時間の文字（「〇分前」。1分未満は1分前、位置がなければ「なし」） */
export function gpsAgoText(lastAt: number | null | undefined, now: number): string {
  if (!lastAt) return 'なし';
  return `${Math.max(1, Math.floor((now - lastAt) / 60000))}分前`;
}

// ---------------------------------------------------------------- 地図の閲覧ログ

export const MAP_VIEW_MIN_INTERVAL_MS = 5000;
const MAP_VIEW_BUFFER_MAX = 200;

export interface BatcherDeps {
  send: (views: MapViewInput[]) => Promise<unknown>;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (h: unknown) => void;
  minIntervalMs?: number;
}

/**
 * 地図の表示範囲の記録（009 log_map_views）。動くたびにためて、送るのは5秒に1回までにまとめる。
 * 送れなかったら黙って捨てる（地図の操作は止めない）
 */
export class MapViewBatcher {
  private buf: MapViewInput[] = [];
  private lastSent = -Infinity;
  private timer: unknown = null;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (h: unknown) => void;
  private readonly gap: number;

  constructor(private readonly deps: BatcherDeps) {
    this.now = deps.now ?? Date.now;
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
    this.gap = deps.minIntervalMs ?? MAP_VIEW_MIN_INTERVAL_MS;
  }

  push(v: MapViewInput): void {
    this.buf.push(v);
    if (this.buf.length > MAP_VIEW_BUFFER_MAX) this.buf.splice(0, this.buf.length - MAP_VIEW_BUFFER_MAX);
    const wait = this.lastSent + this.gap - this.now();
    if (wait <= 0) this.flush();
    else if (this.timer === null) this.timer = this.setTimer(() => this.flush(), wait);
  }

  /** ためた分を送る。5秒の間隔は flush のたびに数え直す */
  flush(): void {
    if (this.timer !== null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
    if (!this.buf.length) return;
    const batch = this.buf;
    this.buf = [];
    this.lastSent = this.now();
    try {
      void this.deps.send(batch).catch(() => undefined);
    } catch {
      /* 送れなくても捨てるだけ */
    }
  }

  get pending(): number {
    return this.buf.length;
  }
}
